/**
 * Sentry initialization wrapper for all EngageXP frontend apps.
 *
 * Usage — call ONCE at the very top of your app entry, before any other
 * imports execute meaningful code:
 *
 * ```ts
 * // main.tsx
 * import { initSentry } from "@engagexp/observability";
 *
 * initSentry({
 *   product: "gamin_index",
 *   dsn: import.meta.env.VITE_SENTRY_DSN,
 *   environment: import.meta.env.PROD ? "production" : "development",
 *   release: import.meta.env.VITE_APP_VERSION,
 * });
 *
 * // then normal imports
 * import { createRoot } from "react-dom/client";
 * ```
 *
 * LGPD: `sendDefaultPii: false` is hardcoded. Sentry will NOT automatically
 * collect user IPs, cookies, or request headers. The only identifier we
 * attach is the Clerk user id, done explicitly via `setSentryUser()` after
 * sign-in.
 */
import * as Sentry from "@sentry/react";
import type { SentryConfig } from "./types";

let initialized = false;

export function initSentry(config: SentryConfig): void {
  if (initialized) return;
  if (config.disabled) return;
  if (!config.dsn) {
    // Missing DSN is not an error — dev environments often skip Sentry.
    return;
  }

  const env = config.environment ?? "development";

  // Sensible defaults per environment. Callers can override.
  const defaultTraces =
    env === "production" ? 0.1 : env === "staging" ? 1.0 : 0;

  Sentry.init({
    dsn: config.dsn,
    environment: env,
    release: config.release,
    // LGPD: never send PII automatically.
    sendDefaultPii: false,
    // Performance tracing.
    tracesSampleRate: config.tracesSampleRate ?? defaultTraces,
    // Session replay — off by default (quota control).
    replaysSessionSampleRate: config.replaysSessionSampleRate ?? 0,
    replaysOnErrorSampleRate: config.replaysOnErrorSampleRate ?? 0,
    // Browser tracing integration (automatic route/fetch instrumentation).
    integrations: [Sentry.browserTracingIntegration()],
    // Tag every event with the product so you can filter across projects.
    initialScope: {
      tags: {
        product: config.product,
        platform: "web",
      },
    },
    // Drop events during development unless explicitly enabled.
    enabled: env !== "development" || Boolean(config.dsn),
  });

  initialized = true;
}

/**
 * Attach the authenticated user's stable ID to Sentry events.
 * Call from your Clerk sign-in bridge with `user.id` — NEVER pass email.
 */
export function setSentryUser(userId: string): void {
  if (!initialized) return;
  Sentry.setUser({ id: userId });
}

/** Clear the Sentry user on sign-out. */
export function clearSentryUser(): void {
  if (!initialized) return;
  Sentry.setUser(null);
}

/** Manually capture an exception. Prefer letting Sentry auto-capture. */
export function captureException(
  error: unknown,
  context?: Record<string, unknown>,
): void {
  if (!initialized) return;
  if (context) {
    Sentry.withScope((scope) => {
      scope.setContext("extra", context);
      Sentry.captureException(error);
    });
  } else {
    Sentry.captureException(error);
  }
}

/** Re-export Sentry's ErrorBoundary so apps don't need a direct dep. */
export const SentryErrorBoundary = Sentry.ErrorBoundary;
